import { HugeiconsIcon } from "@hugeicons/react"
import {
  Settings01Icon,
  ArrowRight01Icon,
  SidebarLeft01Icon,
  SidebarRight01Icon,
} from "@hugeicons/core-free-icons"
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/contexts/AuthContext'
import { cn } from '@/lib/utils'
import { ThemeSwitch } from '@/components/shared/ThemeSwitch'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface UserDropdownProps {
  isCollapsed?: boolean
  /** Collapse / expand the rail. Omit on mobile. */
  onToggleCollapse?: () => void
}

export default function UserDropdown({
  isCollapsed = false,
  onToggleCollapse,
}: UserDropdownProps) {
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const handleLogout = () => {
    logout()
    navigate('/sign-in')
  }

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      const names = user.name.split(' ')
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase()
      }
      return user.name.substring(0, 2).toUpperCase()
    }
    return 'U'
  }

  // Avatar: 28px neutral circle, 11px/600 initials (Paper "App v3" user row).
  const avatar = (
    <div className="flex items-center justify-center size-7 rounded-full bg-[#E5E5E5] dark:bg-white/10 shrink-0">
      <span className="text-xs font-semibold leading-[14px] text-muted-foreground">{getUserInitials()}</span>
    </div>
  )

  // User row: 40px, 6px side padding, 10px gap, name 13px/500 over email 12px, trailing "···".
  const trigger = (
    <button
      type="button"
      aria-label="Account menu"
      className={cn(
        "flex items-center rounded-lg transition-colors shrink-0 text-left",
        "hover:bg-black/[0.035] dark:hover:bg-white/5 data-[state=open]:bg-black/[0.035] dark:data-[state=open]:bg-white/5",
        "outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring/60",
        isCollapsed ? "justify-center size-10 mx-auto" : "h-10 w-full gap-2.5 px-1.5"
      )}
    >
      {avatar}
      {!isCollapsed && (
        <>
          <div className="flex flex-1 flex-col min-w-0">
            <span className="truncate text-[13px] font-medium leading-4 text-foreground">{user?.name || 'User'}</span>
            <span className="truncate text-xs leading-4 text-foreground/60">
              {user?.email || 'user@example.com'}
            </span>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0 fill-foreground/35">
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </>
      )}
    </button>
  )

  const dropdownContent = (
    <DropdownMenuContent
      className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-xl"
      side={isCollapsed ? "right" : "top"}
      align="end"
      sideOffset={6}
    >
      <DropdownMenuLabel className="p-0 font-normal">
        <div className="flex items-center gap-2.5 px-2 py-2 text-left">
          {avatar}
          <div className="grid flex-1 text-left leading-tight">
            <span className="truncate text-[13px] font-medium">{user?.name || 'User'}</span>
            <span className="truncate text-xs text-muted-foreground">
              {user?.email || 'user@example.com'}
            </span>
          </div>
        </div>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => navigate('/user-settings')} className="cursor-pointer text-[13px]">
        <HugeiconsIcon icon={Settings01Icon} size={16} className="mr-2" />
        Account settings
      </DropdownMenuItem>
      <div className="flex items-center justify-between gap-3 px-2 py-1.5">
        <span className="text-[13px] text-foreground">Theme</span>
        <ThemeSwitch compact className="w-[108px]" />
      </div>
      {onToggleCollapse && (
        <DropdownMenuItem onClick={onToggleCollapse} className="cursor-pointer text-[13px]">
          <HugeiconsIcon icon={isCollapsed ? SidebarRight01Icon : SidebarLeft01Icon} size={16} className="mr-2" />
          {isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          <span className="ml-auto text-xs text-muted-foreground">⌘B</span>
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive text-[13px]">
        <HugeiconsIcon icon={ArrowRight01Icon} size={16} className="mr-2 rotate-180" />
        Sign out
      </DropdownMenuItem>
    </DropdownMenuContent>
  )

  if (isCollapsed) {
    return (
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              {trigger}
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" className="text-xs font-medium">
            {user?.name || 'User'}
          </TooltipContent>
        </Tooltip>
        {dropdownContent}
      </DropdownMenu>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger}
      </DropdownMenuTrigger>
      {dropdownContent}
    </DropdownMenu>
  )
}
